import { FC, memo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import classNames from 'classnames'
import { capitalize } from 'lodash'
import { ScrollBarContainer } from 'src/components/ScrollBarContainer'
import { PositivityLegend } from 'src/components/PositivityLegend'
import en from 'src/constants/en'
import { PositivityTypes } from 'src/components/PositivityLegend/enums/positivityTypes.enum'
import { AnalyticsFetchHotLinksResponse } from 'src/services/__generated__/api'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import styles from './hotLinks.module.scss'
import { HotLinksChart } from './HotLinksChart'

// Cards are 214x288 with an 18px radius and a 25px right margin (see
// hotLinks.module.scss `.card`). The section is 770px wide with 40px side
// padding, so roughly three cards are visible at rest — the skeleton mirrors
// that so the strip reserves its real footprint instead of rendering an empty
// zero-height row that jumps open when the data lands.
const CARD_WIDTH = 214
const CARD_HEIGHT = 288
const CARD_RADIUS = 18
const CARD_SKELETON_COUNT = 3

interface HotLinksSectionProps {
  chartData?: AnalyticsFetchHotLinksResponse
  isLoading?: boolean
}

export const HotLinksSection: FC<HotLinksSectionProps> = memo(
  ({ chartData, isLoading }) => {
    const { data: informationTooltips } = useGetInformationTooltipsQuery()

    // Skeletons are for the FIRST load only. On a refetch the previous cards
    // stay on screen and the title spinner reports the activity instead.
    const isInitialLoading = Boolean(isLoading) && !chartData

    return (
      <div className={styles.hotLinksSection}>
        <SectionTitle
          isLoading={isLoading && !isInitialLoading}
          title={en.hotLinks}
          tooltipContent={informationTooltips?.hotLinks}
        />

        <ScrollBarContainer isHorizontal>
          <div className={styles.cardsWrapper}>
            {isInitialLoading &&
              Array.from({ length: CARD_SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  className={styles.cardSkeleton}
                  height={CARD_HEIGHT}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  radius={CARD_RADIUS}
                  width={CARD_WIDTH}
                />
              ))}
            {chartData?.map((item) => (
              <div
                className={styles.card}
                key={`${item.name} - ${item.campaign} - ${item.total} - ${item.date}`}
              >
                <div className={styles.cardHeader}>
                  {item?.source && SOCIAL_ICONS[item.source.toLowerCase()]}

                  <span
                    className={styles.cardTitle}
                    title={platformDisplayLabel(
                      item?.source,
                      capitalize(item?.source)
                    )}
                  >
                    {platformDisplayLabel(
                      item?.source,
                      capitalize(item?.source)
                    )}
                  </span>
                </div>

                <div className={styles.chartWrapper}>
                  <HotLinksChart
                    negatives={item.negatives}
                    negativesPercentage={item.negativePerc}
                    neutrals={item.neutrals}
                    neutralsPercentage={item.neutralPerc}
                    positives={item.positives}
                    positivesPercentage={item.positivePerc}
                  />
                </div>

                <div className={styles.nameWrapper}>
                  <div className={styles.name} title={item?.campaign}>
                    {item?.campaign}
                  </div>

                  <a
                    className={styles.iconWrapper}
                    href={item?.links?.[0]}
                    rel="noreferrer"
                    target="_black"
                  >
                    {item.links?.length ? (
                      <FontAwesomeIcon
                        className={styles.externalLinkIcon}
                        icon={faExternalLinkAlt}
                        title={item?.links?.[0]}
                      />
                    ) : null}
                  </a>
                </div>

                <div className={styles.date}>
                  {moment(item?.date).format('DD.MM.YYYY')}
                </div>

                <div className={styles.cardFooter}>
                  <div className={styles.cardFooterWrapper}>
                    <span className={styles.cardFooterWrapperSpan} />

                    <span>
                      {item?.positives
                        ?.toString()
                        ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </span>
                  </div>

                  <div className={styles.cardFooterWrapper}>
                    <span
                      className={classNames(
                        styles.cardFooterWrapperSpan,
                        styles.neutral
                      )}
                    />

                    <span>
                      {item?.neutrals
                        ?.toString()
                        ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </span>
                  </div>

                  <div className={styles.cardFooterWrapper}>
                    <span
                      className={classNames(
                        styles.cardFooterWrapperSpan,
                        styles.negative
                      )}
                    />

                    <span>
                      {item?.negatives
                        ?.toString()
                        ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollBarContainer>

        <div className={styles.footer}>
          <PositivityLegend
            types={[
              PositivityTypes.POSITIVE,
              PositivityTypes.NEUTRAL,
              PositivityTypes.NEGATIVE,
            ]}
          />
        </div>
      </div>
    )
  }
)

HotLinksSection.displayName = 'HotLinksSection'
